import { useContext, useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import {
  FleetReminderWorkflow,
  ScheduledWorkflow,
  selectAllWorkflows,
} from "@/reducers/openclawWorkflowsSlice"
import { XmppContext } from "@/stream/xmppClient"
import { ChatAutomationScope, workflowsForChat } from "@/lib/chatAutomations"

/** What an automations surface shows, split into the groups it renders. */
export type VisibleWorkflows = {
  /** Scheduled rules I own — the only ones this app may write. */
  personal: ScheduledWorkflow[]
  /** Rules the account shares; someone else owns them, so they stay read-only. */
  shared: ScheduledWorkflow[]
  fleetReminders: FleetReminderWorkflow[]
  /** Rows on screen. The badge and the empty state both read this, so neither
   * can disagree with the list. */
  count: number
}

/**
 * The single answer to "what does the account-wide automations panel show" —
 * the badge, the list and the empty state all read it. A driver room asks
 * `useRoomWorkflows` instead: it groups by who the rule is about, not by who
 * owns it.
 */
export const useVisibleWorkflows = (): VisibleWorkflows => {
  const { myJid } = useContext(XmppContext)
  const storedWorkflows = useAppSelector(selectAllWorkflows)
  const myJidStr = `${myJid.local}@${myJid.getDomain()}`

  return useMemo(() => {
    const personal = storedWorkflows.filter(
      (w): w is ScheduledWorkflow =>
        w.kind === "cron" && w.scope === "local" && w.author === myJidStr,
    )
    const shared = storedWorkflows.filter(
      (w): w is ScheduledWorkflow => w.kind === "cron" && w.scope === "global",
    )
    const fleetReminders = storedWorkflows.filter(
      (w): w is FleetReminderWorkflow => w.kind === "fleet-reminder",
    )

    return {
      personal,
      shared,
      fleetReminders,
      count: personal.length + shared.length + fleetReminders.length,
    }
  }, [storedWorkflows, myJidStr])
}
